import React from 'react';
import styled from 'styled-components';
import {
  FlexBox,
  FlexColumn,
  FlexRow,
  SvgIcon,
  ThemeModeType,
} from '@make-software/cspr-ui';
import { BodyText, Button } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setMode } from '../../redux/theme';

import {
  setNodeLunchSpotlightVisited,
  setThemeSpotlightVisited,
} from '../../redux/feature-spotlights';

export const SpotlightTooltipContainer = styled(FlexColumn)<{ width?: string }>(
  ({ theme, width }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: width || '248px',
    height: 'auto',
    background: theme.styleguideColors.backgroundPrimary,
    padding: '24px',
    borderTop: '5px solid red',
    borderRadius: theme.borderRadius.base,
  })
);

const DarkModeIconContainer = styled(FlexBox)(({ theme }) => ({
  borderRadius: '50%',
  background: theme.styleguideColors.fillPrimaryBlue,
  width: '60px',
  height: '60px',
  color: theme.styleguideColors.contentOnFill,
  zIndex: theme.zIndex.dropdown,
}));

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  svg: {
    path: {
      fill: theme.styleguideColors.contentOnFill,
    },
  },
}));

export const ThemeModeIconPath = {
  [ThemeModeType.light]: 'assets/icons/ic-light-mode.svg',
  [ThemeModeType.dark]: 'assets/icons/ic-dark-mode.svg',
};

export const ThemeModeTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleModeChange = () => {
    dispatch(setMode(ThemeModeType.dark));
    dispatch(setThemeSpotlightVisited(true));

    window.gtag('event', 'click', {
      event_category: 'dark_mode',
      event_label: `Dark mode is selected`,
    });
  };

  return (
    <>
      <DarkModeIconContainer align="center" justify="center">
        <StyledSvgIcon src={ThemeModeIconPath.dark} size={18} />
      </DarkModeIconContainer>
      <BodyText
        scale={'lg'}
        lineHeight={'sm'}
        size={1}
        style={{ marginTop: 16 }}
        variation="black"
      >
        <Trans t={t}>Dark mode is here!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText size={3} scale={'sm'} variation="darkGray">
          <Trans t={t}>
            Now you can enjoy your CSPR.live experience in dark mode. Try it
            now.
          </Trans>
        </BodyText>
        <FlexColumn itemsSpacing={20}>
          <Button color="primaryRed" onClick={handleModeChange}>
            <Trans t={t}>Switch to dark mode</Trans>
          </Button>
          <Button
            color="secondaryBlue"
            onClick={() => dispatch(setThemeSpotlightVisited(true))}
          >
            <Trans t={t}>Maybe later</Trans>
          </Button>
        </FlexColumn>
      </FlexColumn>
    </>
  );
};

export const NewNodeLunchContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <FlexColumn>
      <FlexRow justify={'center'}>
        <SvgIcon src={'assets/images/node-lunch.svg'} size={195} />
      </FlexRow>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>Congratulations on Casper 2.0 upgrade!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          scale={'sm'}
          size={3}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>The Real-World-Ready Blockchain is here!</Trans>
          <br />
          <Trans t={t}>Kudos to the Casper Core Team</Trans>
        </BodyText>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setNodeLunchSpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </FlexColumn>
  );
};

export const SpotlightTooltip = ({ step, tooltipProps }) => {
  return (
    <SpotlightTooltipContainer {...tooltipProps} width={step.width}>
      {step.title && (
        <BodyText size={1} scale={'lg'} lineHeight={'sm'} variation="black">
          {step.title}
        </BodyText>
      )}
      {step.content}
    </SpotlightTooltipContainer>
  );
};
